<template>

  <div class="rate-set-item">

    <div v-if="showIds" class="rate-set-item-id">
      <o-text-field
        label="DR ID"
        :model-value="modelValue.ID"
        disabled
        persistent-placeholder
      />
    </div>

    <template v-if="tiered">

      <div class="rate-set-item-per-quantity">
        <o-text-field
          label="Quantity"
          v-model.number="v$.perQuantity.$model"
          :validator="v$.perQuantity"
          type="number"
          step="0.5"
          :readonly="readonly || !!modelValue.ID"
        />
      </div>

      <div class="rate-set-item-per-unit">
        <o-select
          label="Unit"
          :items="perUnitOptions"
          v-model="v$.perUnit.$model"
          :validator="v$.perUnit"
          :readonly="readonly || !!modelValue.ID"
        />
      </div>

    </template>

    <div class="rate-set-item-cost">
      
      <o-text-field
        label="Default Cost"
        type="number"
        prefix="$"
        :readonly="readonly"
        v-model.number="v$.defaultCost.$model"
        :validator="v$.defaultCost"
        step="0.01"
        required
      />

      <div v-show="channelRatesExpanded">
        <template v-for="(channelDefaultRate, index) in formState.channelDefaultRates">
          <RateFormChannelRate
            v-if="onlyChannelFacilityIds === null || (Array.isArray(onlyChannelFacilityIds) && onlyChannelFacilityIds.includes(channelDefaultRate.channelFacility.ID))"
            ref="channelRateForms"
            v-model="formState.channelDefaultRates[index]"
            :tiered="tiered"
            :all-default-rates="allDefaultRates"
            :readonly="readonly"
            :show-ids="showIds"
            :channel-facility="channelDefaultRate.channelFacility"
          />
        </template>
      </div>

    </div>

    <div class="rate-set-item-expand">
      <v-btn
        :icon="channelRatesExpanded ? (haveChannelRates ? '$expandHighlighted' : '$expand') : (haveChannelRates ? '$collapseHighlighted' : '$collapse')"
        @click="channelRatesExpanded = !channelRatesExpanded"
        :title="channelRatesExpanded ? 'Hide per-channel overrides' : 'Show per-channel overrides'"
      />
    </div>

    <div v-if="tiered && !readonly" class="rate-set-item-tool">
      <v-btn
        icon="$delete"
        title="Remove rate tier"
        @click="$emit('delete')"
        :disabled="!deletable"
      />
    </div>

  </div>

</template>

<script setup>

/**
 * Represents a single DefaultRate, along with any associated ChannelDefaultRates.
 */

import { computed, ref, useTemplateRef } from 'vue'

// components
import RateFormChannelRate from './RateFormChannelRate.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { helpers, requiredIf } from '@vuelidate/validators'
import { required, minValue } from '../utils/i18n-validators'
import { isEmpty } from '../validation'
import dollarAmount from '../rules/dollarAmount'

const props = defineProps({

  modelValue: {
    type: Object,
    required: true,
  },

  readonly: {
    type: Boolean,
    default: false,
  },

  tiered: {
    type: Boolean,
    default: false,
  },

  deletable: {
    type: Boolean,
    default: false,
  },

  showIds: {
    type: Boolean,
    default: false,
  },

  // If provided, only channel_default_rates belonging to the specified
  // channel_facilities will be shown.
  onlyChannelFacilityIds: {
    type: Array,
    default: null,
  },

  // The entire list of DRs in the RateSet
  allDefaultRates: {
    type: Array,
    default: () => [],
  },

  maxTierDurationInHours: {
    type: Number,
    default: null,
  },

})

const emit = defineEmits([
  'update:modelValue',
  'delete',
])

const channelRatesExpanded = ref(false)

const perUnitOptions = [
  { value: 'hour', title: 'Hour(s)' },
  { value: 'day', title: 'Day(s)' },
]

/**
 * Form state and rules
 */

const formState = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// TODO: If we're using a nested component for the individual rateset forms, then we might want to remove validation here.
// But then how do we bubble the validation state up to the main component?

const isMultipleOfHalf = (value) => !helpers.req(value) || (value % 0.5) === 0

const channelRateForms = useTemplateRef('channelRateForms')

const tooLongMessage = computed(() => `Can't be longer than ${props.maxTierDurationInHours} hours`)

const formRules = {
  defaultCost: {
    required,
    minValue: minValue(0.01),
    dollarAmount: helpers.withMessage('Must be a valid dollar amount', dollarAmount),
    doesntHaveCdrErrors: helpers.withMessage('Expand to see errors', () => {
      const hasHiddenCdrErrors = !channelRatesExpanded.value && channelRateForms.value.some(crf => crf.hasError)
      return !hasHiddenCdrErrors
    }),
    notCheaperThanShorterTier: helpers.withMessage('Can\'t be cheaper than a shorter tier', (value) => {

      if (!props.tiered) {
        return true
      }

      const thisDr = formState.value
      const thisDrIsFilledOut = !isEmpty(thisDr.perQuantity) && !isEmpty(thisDr.perUnit) && !isEmpty(thisDr.defaultCost)

      if (!thisDrIsFilledOut) {
        return true
      }

      const thisDrDuration = thisDr.perUnit === 'day' ? thisDr.perQuantity * 24 : thisDr.perQuantity

      const shorterMoreExpensiveDrsExist = props.allDefaultRates
        .filter(dr => dr !== thisDr)
        .filter(otherDr => !isEmpty(otherDr.perQuantity) && !isEmpty(otherDr.perUnit) && !isEmpty(otherDr.defaultCost))
        .some(otherDr => {
          const otherDrDuration = otherDr.perUnit === 'day' ? otherDr.perQuantity * 24 : otherDr.perQuantity
          const otherDrIsShorter = otherDrDuration < thisDrDuration
          const otherDrIsMoreExpensive = otherDr.defaultCost > thisDr.defaultCost
          return otherDrIsShorter && otherDrIsMoreExpensive
      })

      return !shorterMoreExpensiveDrsExist

    }),
    spotheroCannotBeMoreThan250: helpers.withMessage('This would set a SpotHero channel rate to > $250', (value) => {
      if (!value || value <= 250) {
        return true
      }
      const haveUnsetSpotheroCdrs = formState.value.channelDefaultRates.some(cdr => {
        const cdrIsShown = props.onlyChannelFacilityIds === null || (Array.isArray(props.onlyChannelFacilityIds) && props.onlyChannelFacilityIds.includes(cdr.channelFacility.ID))
        return cdrIsShown && cdr.channelFacility.channel === 'spothero' && !cdr.channelCost
      })
      return !haveUnsetSpotheroCdrs
    }),
  },
  perQuantity: {
    requiredIf: helpers.withMessage('Required', requiredIf(() => props.tiered)),
    isMultipleOfHalf: helpers.withMessage('Must be a multiple of 0.5', isMultipleOfHalf),
    minValue: minValue(0.5),
    maxDuration: helpers.withMessage(tooLongMessage, (value) => {
      if (!helpers.req(value)) {
        return true
      }
      if (!props.maxTierDurationInHours) {
        return true
      }
      if (!formState.value.perUnit) {
        return `Can't exceed ${props.maxTierDurationInHours} hour(s)`
      }
      const durationInHours = value * (formState.value.perUnit === 'day' ? 24 : 1)
      return durationInHours <= props.maxTierDurationInHours
    }),
  },
  perUnit: {
    requiredIf: helpers.withMessage('Required', requiredIf(() => props.tiered)),
  },
}

const v$ = useVuelidate(formRules, formState)

const haveChannelRates = computed(() => {

  return formState.value.channelDefaultRates.some((channelDefaultRate) => {
    return channelDefaultRate.channelCost !== '' && channelDefaultRate.channelCost !== null
  })

})

</script>
